/// <reference types="vite/client" />
/**
 * Video Registry - Global registry for video endpoint lookups
 *
 * Stores canvas/renderer pairs. Canvas and renderer may arrive from different
 * places at different times - when both are present, renderer.setCanvas is called.
 */

import { logger } from '@/logging';
import type { ICanvasRenderer } from './nodes/VideoCanvasNode';
import { VideoEndpoint } from './VideoEndpoint';

// ─────────────────────────────────────────────────────────────
// Video Entry - stores canvas and renderer pair
// ─────────────────────────────────────────────────────────────

export class VideoEntry {
  private canvas: HTMLCanvasElement | null = null;
  private renderer: ICanvasRenderer | null = null;
  private size: [number, number] | null = null;

  setCanvas(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;
    if (this.renderer) {
      this.renderer.setCanvas(canvas);
    }
  }

  setRenderer(renderer: ICanvasRenderer): void {
    this.renderer = renderer;
    if (this.canvas) {
      this.renderer.setCanvas(this.canvas);
    }
    if (this.size) {
      this.renderer.setSize(this.size[0], this.size[1]);
    }
  }

  setSize(width: number, height: number): void {
    this.size = [width, height];
    if (this.renderer) {
      this.renderer.setSize(width, height);
    }
  }

  getCanvas(): HTMLCanvasElement | null {
    if (this.canvas) return this.canvas;
    if (this.renderer) return this.renderer.getCanvas();
    return null;
  }

  getRenderer(): ICanvasRenderer | null {
    return this.renderer;
  }

  getSize(): [number, number] | null {
    return this.size;
  }

  clear(): void {
    this.canvas = null;
    this.renderer = null;
    this.size = null;
  }
}

// ─────────────────────────────────────────────────────────────
// Registry Storage
// ─────────────────────────────────────────────────────────────

/** Global registry of VideoEntries */
const videoRegistry: Map<string, VideoEntry> = new Map();

/**
 * Build registry key for a video node.
 * Only one renderer per node, so endpointId is not needed.
 */
export function makeVideoKey(instanceId: string, nodeId: string): string {
  return `${instanceId}.${nodeId}`;
}

/**
 * Get or create a VideoEntry for the given node.
 */
function getEntry(instanceId: string, nodeId: string): VideoEntry | undefined {
  const key = makeVideoKey(instanceId, nodeId);
  return videoRegistry.get(key);
}

/**
 * Get or create a VideoEntry for the given node.
 * If an entry already exists (e.g. Repeater re-creating children), clear and reuse it.
 */
function createEntry(instanceId: string, nodeId: string): VideoEntry {
  const key = makeVideoKey(instanceId, nodeId);
  let entry = videoRegistry.get(key);

  if (entry) {
    entry.clear();
    return entry;
  }
  entry = new VideoEntry();
  videoRegistry.set(key, entry);

  return entry;
}

// ─────────────────────────────────────────────────────────────
// Registration
// ─────────────────────────────────────────────────────────────

/**
 * Register a canvas for a video node.
 * Retries if the entry doesn't exist yet (UI may mount before renderer registers).
 */
export function setVideoCanvas(
  instanceId: string,
  nodeId: string,
  canvas: HTMLCanvasElement,
  retryCount = 0
): void {
  const entry = getEntry(instanceId, nodeId);
  if (entry) {
    entry.setCanvas(canvas);
    return;
  }

  // Entry doesn't exist yet - retry after a short delay
  if (retryCount < 10) {
    setTimeout(() => {
      setVideoCanvas(instanceId, nodeId, canvas, retryCount + 1);
    }, 50);
  } else {
    logger.trace('videoRegistry', `No renderer registered for ${instanceId}.${nodeId} — canvas not connected`);
  }
}

export function getCanvasFromVideoRegistry(endpoint: VideoEndpoint, retryCount = 0): Promise<HTMLCanvasElement> {
  // handle subgraph internal mappings
  while (endpoint.internalEndpoint instanceof VideoEndpoint) {
    endpoint = endpoint.internalEndpoint;
  }

  const entry = getVideoEntry(endpoint.instancePath, endpoint.node.id);
  const canvas = entry?.getCanvas();

  if (canvas) {

    logger.trace('getCanvasFromVideoRegistry', `found canvas at ${endpoint.instancePath}.${endpoint.node.id}`);
    return Promise.resolve(canvas);
  } else if (retryCount < 10) {
    // Canvas not ready yet, retry
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        logger.trace('getCanvasFromVideoRegistry', `retrying canvas at ${endpoint.instancePath}.${endpoint.node.id}`);
        getCanvasFromVideoRegistry(endpoint, retryCount + 1)
          .then(resolve)
          .catch(reject);
      }, 100);
    });
  } else {
    logger.trace('getCanvasFromVideoRegistry', `gave up waiting for canvas at ${endpoint.instancePath}.${endpoint.node.id}`);
    return Promise.reject(new Error('Canvas not available'));
  }
}




/**
 * Register a renderer for a video node.
 */
export function registerVideoRenderer(
  instanceId: string,
  nodeId: string,
  renderer: ICanvasRenderer
): void {

  createEntry(instanceId, nodeId).setRenderer(renderer);
}

/**
 * Set the size for a video node.
 */
export function setVideoSize(
  instanceId: string,
  nodeId: string,
  width: number,
  height: number
): void {
  getEntry(instanceId, nodeId)?.setSize(width, height);
}

/**
 * Unregister a video entry from the global registry.
 */
export function unregisterVideoEntry(
  instanceId: string,
  nodeId: string
): void {
  const key = makeVideoKey(instanceId, nodeId);
  const entry = videoRegistry.get(key);
  if (entry) {
    entry.clear();
    videoRegistry.delete(key);
  }
}

// ─────────────────────────────────────────────────────────────
// Lookups
// ─────────────────────────────────────────────────────────────

/**
 * Get a VideoEntry by path.
 */
export function getVideoEntry(
  instanceId: string,
  nodeId: string
): VideoEntry | undefined {
  return videoRegistry.get(makeVideoKey(instanceId, nodeId));
}

/**
 * Get a canvas renderer by path.
 */
export function getVideoRenderer(
  instanceId: string,
  nodeId: string
): ICanvasRenderer | undefined {
  return videoRegistry.get(makeVideoKey(instanceId, nodeId))?.getRenderer() ?? undefined;
}

/**
 * Get all registered video entries (for batch queries).
 */
export function getAllVideoEntries(): Map<string, VideoEntry> {
  return videoRegistry;
}

/**
 * Clear all entries from the registry.
 */
export function clearVideoRegistry(): void {
  for (const entry of videoRegistry.values()) {
    entry.clear();
  }
  videoRegistry.clear();
}

// ─────────────────────────────────────────────────────────────
// HMR Cleanup
// ─────────────────────────────────────────────────────────────

// @ts-ignore - Vite HMR, not available in commonjs but Vite transforms it
if (import.meta.hot) {
  // @ts-ignore
  import.meta.hot.dispose(() => {
    clearVideoRegistry();
  });
}
